import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SLICE_NAME } from './constants';
import {
  Answer,
  AnswerType,
  Question,
  Questionnaire,
  QuestionnairesContainerState,
  QuestionSeries,
} from './questionnaires';
import { cloneDeep } from 'lodash';

const initialState: QuestionnairesContainerState = {
  list: [],
  page: 0,
  pageSize: 20,
  total: 0,
  isFetching: false,
  search: '',
  showQuestionnaireDetails: false,
  seriesToDelete: [],
};

export const questionnairesSlice = createSlice({
  name: SLICE_NAME,

  initialState,

  reducers: {
    setList: (state, action: PayloadAction<Questionnaire[]>) => {
      state.list = action.payload;
    },
    addQuestionnaire: (state, action: PayloadAction<Questionnaire[]>) => {
      state.list = state.list.concat(action.payload);
    },
    deleteQuestionnaire: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((s) => s.id !== action.payload);
      state.total = state.total - 1;
    },
    updateQuestionnaireById: (
      state,
      action: PayloadAction<Partial<Questionnaire> & { id: string }>,
    ) => {
      const index = state.list.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        const sp = state.list[index];
        Object.assign(sp, action.payload);
        state.list[index] = sp;
      }
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setActiveQuestionnaire: (
      state,
      action: PayloadAction<Questionnaire | undefined>,
    ) => {
      state.activeQuestionnaire = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setShowQuestionnaireDetails: (state, action: PayloadAction<boolean>) => {
      state.showQuestionnaireDetails = action.payload;
    },
    setSeriesToDelete: (state, action: PayloadAction<string[]>) => {
      state.seriesToDelete = action.payload;
    },
    editSection: (
      state,
      action: PayloadAction<{ section: QuestionSeries; index: number }>,
    ) => {
      const { index, section } = action.payload;
      if (state.activeQuestionnaire) {
        state.activeQuestionnaire.questionSeries[index] = section;
      }
    },
    editQuestion: (
      state,
      action: PayloadAction<{
        question: Question;
        seriesIdx: number;
        questionIdx: number;
      }>,
    ) => {
      const { seriesIdx, questionIdx, question } = action.payload;
      if (state.activeQuestionnaire) {
        state.activeQuestionnaire.questionSeries[seriesIdx].questions[
          questionIdx
        ] = question;
      }
    },
    editAnswer: (
      state,
      action: PayloadAction<{
        answer: Answer;
        seriesIdx: number;
        questionIdx: number;
        answerIdx: number;
      }>,
    ) => {
      const { seriesIdx, questionIdx, answerIdx, answer } = action.payload;
      if (state.activeQuestionnaire) {
        state.activeQuestionnaire.questionSeries[seriesIdx].questions[
          questionIdx
        ].answers[answerIdx] = answer;
      }
    },
    addQuestion: (
      state,
      action: PayloadAction<{ seriesIdx: number; questionIdx: number }>,
    ) => {
      const { seriesIdx, questionIdx } = action.payload;
      if (state.activeQuestionnaire) {
        const newQuestion: Question = {
          answerType: AnswerType.RadioButton,
          answers: [],
          preText: '',
          question: '',
        };
        state.activeQuestionnaire.questionSeries[
          seriesIdx - 1
        ].questions.splice(questionIdx, 0, newQuestion);
      }
    },
    addAnswer: (
      state,
      action: PayloadAction<{ seriesIdx: number; questionIdx: number }>,
    ) => {
      const { seriesIdx, questionIdx } = action.payload;
      if (state.activeQuestionnaire) {
        const newAnswer: Answer = {
          text: '',
          score: 0,
        };
        state.activeQuestionnaire.questionSeries[seriesIdx].questions[
          questionIdx
        ].answers.push(newAnswer);
      }
    },
    removeSection: (state, action: PayloadAction<{ seriesIdx: number }>) => {
      const { seriesIdx } = action.payload;
      if (state.activeQuestionnaire) {
        const id = state.activeQuestionnaire.questionSeries[seriesIdx].id;
        if (id) {
          state.seriesToDelete.push(id);
        }
        state.activeQuestionnaire.questionSeries.splice(seriesIdx, 1);
      }
    },
    removeQuestion: (
      state,
      action: PayloadAction<{ seriesIdx: number; questionIdx: number }>,
    ) => {
      const { seriesIdx, questionIdx } = action.payload;
      if (state.activeQuestionnaire) {
        state.activeQuestionnaire.questionSeries[seriesIdx].questions.splice(
          questionIdx,
          1,
        );
      }
    },
    removeAnswer: (
      state,
      action: PayloadAction<{
        seriesIdx: number;
        questionIdx: number;
        answerIdx: number;
      }>,
    ) => {
      const { seriesIdx, questionIdx, answerIdx } = action.payload;
      if (state.activeQuestionnaire) {
        state.activeQuestionnaire.questionSeries[seriesIdx].questions[
          questionIdx
        ].answers.splice(answerIdx, 1);
      }
    },
    copySection: (state, action: PayloadAction<{ seriesIdx: number }>) => {
      const { seriesIdx } = action.payload;
      if (state.activeQuestionnaire) {
        const newSection = cloneDeep(
          state.activeQuestionnaire.questionSeries[seriesIdx],
        );
        delete newSection.id;
        newSection.questions.forEach((q) => {
          delete q.id;
          q.answers.forEach((a) => delete a.id);
        });
        state.activeQuestionnaire.questionSeries.splice(
          seriesIdx + 1,
          0,
          newSection,
        );
      }
    },
    copyQuestion: (
      state,
      action: PayloadAction<{ seriesIdx: number; questionIdx: number }>,
    ) => {
      const { seriesIdx, questionIdx } = action.payload;
      if (state.activeQuestionnaire) {
        const newQuestion = cloneDeep(
          state.activeQuestionnaire.questionSeries[seriesIdx].questions[
            questionIdx
          ],
        );
        delete newQuestion.id;
        newQuestion.answers.forEach((a) => {
          delete a.id;
        });
        state.activeQuestionnaire.questionSeries[seriesIdx].questions.splice(
          questionIdx + 1,
          0,
          newQuestion,
        );
      }
    },
  },
});

export const {
  setList,
  addQuestionnaire,
  deleteQuestionnaire,
  updateQuestionnaireById,
  setPage,
  setActiveQuestionnaire,
  setSearch,
  setTotal,
  setIsFetching,
  setShowQuestionnaireDetails,
  setSeriesToDelete,
  editSection,
  editQuestion,
  editAnswer,
  addQuestion,
  addAnswer,
  removeSection,
  removeAnswer,
  removeQuestion,
  copySection,
  copyQuestion,
} = questionnairesSlice.actions;

export default questionnairesSlice.reducer;
